import React from "react";
import SettingsSection from "../SettingsSection";
import ChannelPicker from "../ChannelPicker";
import {
  LOG_ACTION_GROUPS,
  actionsForGroup,
} from "../../data/logActions";

export default function GuardianSection({
  channels,
  logs,
  setLogs,
  modLogChannel,
  setModLogChannel,
  messageLogChannel,
  setMessageLogChannel,
  commandToggles,
  onToggleCommand,
  setCommandToggles,
  banDmEnabled,
  setBanDmEnabled,
  banAppealsEnabled,
  setBanAppealsEnabled,
  banAppealChannelId,
  setBanAppealChannelId,
  banUnbanServerEnabled,
  setBanUnbanServerEnabled,
  banUnbanServerInvite,
  setBanUnbanServerInvite,
  banUnbanServerMessage,
  setBanUnbanServerMessage,
  setHasUnsavedChanges,
}) {
  const markDirty = () => {
    if (setHasUnsavedChanges) setHasUnsavedChanges(true);
  };

  const logTypes = [
    {
      key: "mod",
      label: "MODERATION LOGS",
      help: "Bans, kicks, warns, jail, purge.",
    },
    {
      key: "automod",
      label: "AUTOMOD LOGS",
      help: "Forbidden words, anti-link, spam, mention limits. Leave empty to use Moderation logs.",
    },
    {
      key: "admin",
      label: "ADMIN LOGS",
      help: "Settings changes, shop edits, owner commands.",
    },
    {
      key: "message",
      label: "MESSAGE LOGS",
      help: "Edited and deleted messages.",
    },
    {
      key: "joinLeave",
      label: "JOIN / LEAVE LOGS",
      help: "Member joins, leaves, and kicks. Leave empty to use Member logs.",
    },
    {
      key: "member",
      label: "MEMBER LOGS",
      help: "Role and nickname updates.",
    },
    {
      key: "voice",
      label: "VOICE LOGS",
      help: "Voice join, leave, and channel moves.",
    },
    {
      key: "server",
      label: "SERVER LOGS",
      help: "Channels, roles, emojis, stickers, webhooks, invites, and threads.",
    },
    {
      key: "report",
      label: "REPORT LOGS",
      help: "User-submitted incident reports.",
    },
  ];

  const updateLogChannel = (key, channelId) => {
    setLogs((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        channelId,
        ...(channelId ? { enabled: true } : {}),
      },
    }));
    if (key === "mod") setModLogChannel(channelId);
    if (key === "message") setMessageLogChannel(channelId);
  };

  const toggleLogEnabled = (key) => {
    setLogs((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        enabled: prev[key]?.enabled === false,
      },
    }));
  };

  const updateLogActionChannel = (actionKey, channelId) => {
    setLogs((prev) => {
      const actions = { ...(prev.actions || {}) };
      if (channelId) {
        actions[actionKey] = {
          ...(actions[actionKey] || {}),
          channelId,
          enabled: actions[actionKey]?.enabled !== false,
        };
      } else {
        const next = { ...(actions[actionKey] || {}) };
        delete next.channelId;
        if (Object.keys(next).length) actions[actionKey] = next;
        else delete actions[actionKey];
      }
      return { ...prev, actions };
    });
  };

  return (
    <SettingsSection>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Staff moderation tools, log channels, and command toggles. Filters and
        join guard live under <b>Auto Moderation</b>. Also configurable via{" "}
        <code>/settings</code> in Discord.
      </p>
      <div
        style={{
          display: "flex",
          gap: "20px",
          marginBottom: "20px",
          padding: "15px",
          background: "rgba(184, 92, 110, 0.05)",
          borderRadius: "8px",
          border: "1px solid rgba(184, 92, 110, 0.1)",
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={commandToggles?.enabled}
            onChange={() => {
              setCommandToggles((prev) => ({
                ...prev,
                enabled: !prev.enabled,
              }));
              markDirty();
            }}
            style={{ accentColor: "#b85c6e" }}
          />
          <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
            MODERATION ENABLED
          </span>
        </label>
      </div>

      <hr
        style={{
          border: "0",
          borderTop: "1px solid rgba(123, 111, 98, 0.2)",
          marginBottom: "20px",
        }}
      />

      <label className="input-label">BAN NOTICES &amp; APPEALS</label>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "12px" }}
      >
        Kami DMs the user <strong>before</strong> a <code>/ban</code> when
        possible. Manual Discord bans are best-effort after the fact. Appeals
        add an <strong>Apply for unban</strong> button in that DM.
      </p>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          marginBottom: "20px",
          padding: "15px",
          background: "rgba(184, 92, 110, 0.05)",
          borderRadius: "8px",
          border: "1px solid rgba(184, 92, 110, 0.1)",
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={banDmEnabled !== false}
            onChange={() => {
              setBanDmEnabled(!(banDmEnabled !== false));
              markDirty();
            }}
            style={{ accentColor: "#b85c6e" }}
          />
          <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
            DM USERS WHEN BANNED
          </span>
        </label>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={Boolean(banAppealsEnabled)}
            onChange={() => {
              setBanAppealsEnabled(!banAppealsEnabled);
              markDirty();
            }}
            style={{ accentColor: "#b85c6e" }}
          />
          <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
            ENABLE UNBAN APPEALS
          </span>
        </label>
        {banAppealsEnabled ? (
          <div>
            <label className="input-label">APPEALS CHANNEL</label>
            <p
              className="help-text"
              style={{
                color: "#7b6f62",
                fontSize: "0.8rem",
                marginBottom: "8px",
              }}
            >
              Staff-only channel where appeal embeds land with Approve / Deny
              buttons.
            </p>
            <ChannelPicker
              channels={channels}
              value={banAppealChannelId || ""}
              onChange={(channelId) => {
                setBanAppealChannelId(channelId);
                markDirty();
              }}
              emptyLabel="Select appeals channel..."
            />
          </div>
        ) : null}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={Boolean(banUnbanServerEnabled)}
            onChange={() => {
              setBanUnbanServerEnabled(!banUnbanServerEnabled);
              markDirty();
            }}
            style={{ accentColor: "#b85c6e" }}
          />
          <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
            DM UNBAN SERVER INVITE
          </span>
        </label>
        {banUnbanServerEnabled ? (
          <div style={{ display: "grid", gap: "12px" }}>
            <div>
              <label className="input-label">UNBAN SERVER INVITE</label>
              <p
                className="help-text"
                style={{
                  color: "#7b6f62",
                  fontSize: "0.8rem",
                  marginBottom: "8px",
                }}
              >
                Discord invite for your appeals / unban server (e.g.
                discord.gg/xxxx). Added as a button in the ban DM.
              </p>
              <input
                type="text"
                className="settings-input"
                value={banUnbanServerInvite || ""}
                placeholder="https://discord.gg/your-unban-server"
                onChange={(e) => {
                  setBanUnbanServerInvite(e.target.value);
                  markDirty();
                }}
              />
            </div>
            <div>
              <label className="input-label">CUSTOM MESSAGE (OPTIONAL)</label>
              <p
                className="help-text"
                style={{
                  color: "#7b6f62",
                  fontSize: "0.8rem",
                  marginBottom: "8px",
                }}
              >
                Shown in the ban DM above the invite button. Placeholders:{" "}
                {"{server}"}, {"{reason}"}, {"{invite}"}, {"{moderator}"}
              </p>
              <textarea
                className="settings-input"
                rows={4}
                value={banUnbanServerMessage || ""}
                placeholder="You can join our unban appeal server to request a review:&#10;{invite}"
                onChange={(e) => {
                  setBanUnbanServerMessage(e.target.value);
                  markDirty();
                }}
              />
            </div>
          </div>
        ) : null}
      </div>

      <hr
        style={{
          border: "0",
          borderTop: "1px solid rgba(123, 111, 98, 0.2)",
          marginBottom: "20px",
        }}
      />

      <label className="input-label">SERVER LOG CHANNELS</label>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Route each log type to its own channel. Uncheck <b>Enabled</b> to turn a
        stream off without clearing its channel. Also in Discord:{" "}
        <code>/settings</code> → Logs → <b>Enable or disable a log type</b>.
      </p>

      {logTypes.map(({ key, label, help }) => (
        <div key={key} style={{ marginBottom: "18px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              marginBottom: "8px",
            }}
          >
            <label className="input-label" style={{ marginBottom: 0 }}>
              {label}
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                fontSize: "0.8rem",
              }}
            >
              <input
                type="checkbox"
                checked={logs?.[key]?.enabled !== false}
                onChange={() => {
                  toggleLogEnabled(key);
                  markDirty();
                }}
                style={{ accentColor: "#b85c6e" }}
              />
              Enabled
            </label>
          </div>
          <p
            className="help-text"
            style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "8px" }}
          >
            {help}
          </p>
          <ChannelPicker
            channels={channels}
            value={logs?.[key]?.channelId || ""}
            onChange={(channelId) => {
              updateLogChannel(key, channelId);
              markDirty();
            }}
            disabled={logs?.[key]?.enabled === false}
            emptyLabel="Select a channel..."
          />

          {key === "message" && logs?.message?.enabled !== false && (
            <div style={{ marginTop: "14px" }}>
              <label className="input-label">SENSITIVE MESSAGE LOG CHANNEL</label>
              <p
                className="help-text"
                style={{
                  color: "#7b6f62",
                  fontSize: "0.8rem",
                  marginBottom: "8px",
                }}
              >
                Edits/deletes from the channels selected below go here instead
                of the normal message log - use an admin-only channel for staff
                or private areas.
              </p>
              <ChannelPicker
                channels={channels}
                value={logs?.message?.sensitiveChannelId || ""}
                onChange={(channelId) => {
                  setLogs((prev) => ({
                    ...prev,
                    message: {
                      ...prev.message,
                      sensitiveChannelId: channelId,
                    },
                  }));
                  markDirty();
                }}
                emptyLabel="None (use normal message log)"
              />

              <label className="input-label" style={{ marginTop: "12px" }}>
                CHANNELS ROUTED TO SENSITIVE LOG
              </label>
              <ChannelPicker
                channels={channels}
                multiple
                value={logs?.message?.sensitiveSourceChannelIds || []}
                onChange={(ids) => {
                  setLogs((prev) => ({
                    ...prev,
                    message: {
                      ...prev.message,
                      sensitiveSourceChannelIds: ids,
                    },
                  }));
                  markDirty();
                }}
                placeholder="Select channels..."
                allowEmpty
              />
            </div>
          )}
        </div>
      ))}

      <hr
        style={{
          border: "0",
          borderTop: "1px solid rgba(123, 111, 98, 0.2)",
          margin: "24px 0 20px",
        }}
      />

      <label className="input-label">PER-ACTION LOG ROUTES</label>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Optional overrides: send a specific event to its own channel. If unset,
        the log type above is used (for example all moderation → Mod logs). In
        Discord: <code>/settings</code> → Logs → <b>Action routes</b>.
      </p>

      {Object.entries(LOG_ACTION_GROUPS).map(([groupKey, group]) => {
        const items = actionsForGroup(groupKey);
        if (!items.length) return null;
        return (
          <details
            key={groupKey}
            style={{
              marginBottom: "12px",
              padding: "10px 12px",
              borderRadius: "8px",
              border: "1px solid rgba(123, 111, 98, 0.2)",
              background: "rgba(0,0,0,0.08)",
            }}
          >
            <summary
              style={{ cursor: "pointer", fontWeight: 600, fontSize: "0.85rem" }}
            >
              {group.label}
            </summary>
            <div style={{ marginTop: "12px" }}>
              {items.map(({ key, label }) => (
                <div key={key} style={{ marginBottom: "14px" }}>
                  <label
                    className="input-label"
                    style={{ fontSize: "0.75rem", marginBottom: "6px" }}
                  >
                    {label}
                  </label>
                  <ChannelPicker
                    channels={channels}
                    value={logs?.actions?.[key]?.channelId || ""}
                    onChange={(channelId) => {
                      updateLogActionChannel(key, channelId);
                      markDirty();
                    }}
                    emptyLabel="Use category default"
                  />
                </div>
              ))}
            </div>
          </details>
        );
      })}

      <hr
        style={{
          border: "0",
          borderTop: "1px solid rgba(123, 111, 98, 0.2)",
          marginBottom: "20px",
        }}
      />

      <label className="input-label">MODERATOR COMMANDS</label>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Manual powers available to your staff.
      </p>

      <div
        className="command-toggles-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          gap: "10px",
          marginBottom: "30px",
          background: "rgba(0,0,0,0.1)",
          padding: "15px",
          borderRadius: "8px",
          opacity: commandToggles?.enabled ? 1 : 0.5,
          pointerEvents: commandToggles?.enabled ? "all" : "none",
        }}
      >
        {commandToggles?.commands &&
          Object.keys(commandToggles.commands).map((cmd) => (
            <div
              key={cmd}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "5px 10px",
                background: "rgba(255,255,255,0.03)",
                borderRadius: "4px",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <span
                style={{
                  fontSize: "0.85rem",
                  textTransform: "capitalize",
                  fontWeight: "500",
                }}
              >
                {cmd === "clearhistory" ? "Clear History" : cmd}
              </span>
              <input
                type="checkbox"
                checked={commandToggles.commands[cmd]}
                onChange={() => onToggleCommand(cmd)}
                style={{ cursor: "pointer", accentColor: "#b85c6e" }}
              />
            </div>
          ))}
      </div>
    </SettingsSection>
  );
}
